import type * as React from "react";

import { cn } from "./cn";
import { buttonVariants } from "./recipes";
import { Skeleton } from "./skeleton";
import { Spinner } from "./spinner";

/*
 * The three states every data surface has besides "loaded": empty, failed,
 * loading. Each is a block that sits where the content would (inside a card
 * or on the page), never a card of its own.
 */

/**
 * Said when a caller's recovery sentence is blank: every error tells the
 * reader what to do next, even one written in a hurry.
 */
const FALLBACK_RECOVERY =
  "Refresh the page or try again in a minute. If it keeps failing, write to support@nonresident.tax.";

const iconTile =
  "flex size-12 shrink-0 items-center justify-center rounded-card [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-6";

type EmptyStateProps = Omit<React.ComponentProps<"div">, "title"> & {
  /** A glyph for the icon tile (tint fill, accent glyph — the service accent in force). */
  icon?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** The next step: usually one button or link. */
  action?: React.ReactNode;
};

function EmptyState({
  icon,
  title,
  description,
  action,
  className,
  ...props
}: EmptyStateProps) {
  return (
    <div
      data-slot="empty-state"
      className={cn(
        "flex w-full flex-col items-center gap-4 px-6 py-10 text-center",
        className,
      )}
      {...props}
    >
      {icon ? (
        <div
          data-slot="empty-state-icon"
          aria-hidden="true"
          className={cn(iconTile, "bg-service-tint text-service")}
        >
          {icon}
        </div>
      ) : null}
      <div className="flex max-w-sm flex-col items-center gap-1.5">
        <p
          data-slot="empty-state-title"
          className="type-title text-balance text-foreground"
        >
          {title}
        </p>
        {description ? (
          <div
            data-slot="empty-state-description"
            className="type-small text-pretty text-muted-foreground"
          >
            {description}
          </div>
        ) : null}
      </div>
      {action ? <div data-slot="empty-state-action">{action}</div> : null}
    </div>
  );
}

type ErrorStateProps = Omit<React.ComponentProps<"div">, "title"> & {
  /** What failed, in a few words: "We could not load your documents". */
  title: React.ReactNode;
  /** What happened, when there is more to say than the title. */
  description?: React.ReactNode;
  /**
   * REQUIRED: how to recover — "Refresh the page or try again in a minute. If
   * it keeps failing, write to support@nonresident.tax." A blank sentence
   * falls back to exactly that.
   */
  recovery: React.ReactNode;
  /** Shows a "Try again" button that calls this. */
  onRetry?: () => void;
  retryLabel?: string;
  /** Another next step (a link to support, a way back), beside the retry. */
  action?: React.ReactNode;
  icon?: React.ReactNode;
};

function isBlank(node: React.ReactNode): boolean {
  if (node === null || node === undefined || typeof node === "boolean")
    return true;
  if (typeof node === "string") return node.trim().length === 0;
  return false;
}

function ErrorGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.75" />
      <path
        d="M12 7.5v5.25"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
      <circle cx="12" cy="16.25" r="1.1" fill="currentColor" />
    </svg>
  );
}

function ErrorState({
  title,
  description,
  recovery,
  onRetry,
  retryLabel = "Try again",
  action,
  icon,
  className,
  ...props
}: ErrorStateProps) {
  const hasActions = Boolean(onRetry) || Boolean(action);
  return (
    <div
      data-slot="error-state"
      role="alert"
      className={cn(
        "flex w-full flex-col items-center gap-4 px-6 py-10 text-center",
        className,
      )}
      {...props}
    >
      <div
        data-slot="error-state-icon"
        aria-hidden="true"
        className={cn(iconTile, "bg-danger-tint text-danger")}
      >
        {icon ?? <ErrorGlyph />}
      </div>
      <div className="flex max-w-md flex-col items-center gap-1.5">
        <p
          data-slot="error-state-title"
          className="type-title text-balance text-foreground"
        >
          {title}
        </p>
        {description ? (
          <div
            data-slot="error-state-description"
            className="type-small text-pretty text-body"
          >
            {description}
          </div>
        ) : null}
        <div
          data-slot="error-state-recovery"
          className="type-small text-pretty text-muted-foreground"
        >
          {isBlank(recovery) ? FALLBACK_RECOVERY : recovery}
        </div>
      </div>
      {hasActions ? (
        <div
          data-slot="error-state-actions"
          className="flex flex-wrap items-center justify-center gap-2"
        >
          {onRetry ? (
            <button
              type="button"
              onClick={onRetry}
              className={cn(
                buttonVariants({ variant: "secondary", size: "sm" }),
              )}
            >
              {retryLabel}
            </button>
          ) : null}
          {action}
        </div>
      ) : null}
    </div>
  );
}

type LoadingStateProps = React.ComponentProps<"div"> & {
  /** `skeleton` (default): bars where the rows will be. `spinner`: a ring and the label, for a wait with no shape. */
  variant?: "skeleton" | "spinner";
  /** How many skeleton rows. */
  rows?: number;
  /** What is loading; shown beside the spinner, read to screen readers either way. */
  label?: string;
};

const ROW_WIDTHS = ["w-full", "w-11/12", "w-4/5", "w-2/3"] as const;

function LoadingState({
  variant = "skeleton",
  rows = 3,
  label = "Loading",
  className,
  ...props
}: LoadingStateProps) {
  if (variant === "spinner") {
    return (
      <div
        data-slot="loading-state"
        role="status"
        aria-live="polite"
        className={cn(
          "flex w-full items-center justify-center gap-2.5 px-6 py-10 type-small text-muted-foreground",
          className,
        )}
        {...props}
      >
        <Spinner
          aria-hidden="true"
          role="presentation"
          aria-label={undefined}
        />
        <span>{label}</span>
      </div>
    );
  }
  return (
    <div
      data-slot="loading-state"
      role="status"
      aria-live="polite"
      aria-busy="true"
      className={cn("flex w-full flex-col gap-3 py-2", className)}
      {...props}
    >
      <span className="sr-only">{label}</span>
      {Array.from({ length: Math.max(1, rows) }, (_, index) => (
        <div key={index} aria-hidden="true" className="flex items-center gap-3">
          <Skeleton className="size-9 shrink-0 rounded-control" />
          <div className="flex flex-1 flex-col gap-2">
            <Skeleton
              className={cn("h-3.5", ROW_WIDTHS[index % ROW_WIDTHS.length])}
            />
            <Skeleton className="h-3 w-1/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

export { EmptyState, ErrorState, FALLBACK_RECOVERY, LoadingState };
export type { EmptyStateProps, ErrorStateProps, LoadingStateProps };
